import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, ChevronsUp, Minus, X } from 'lucide-react';
import type { NguoiDung } from '../../server/kieu';
import { thongTinTt, UU_TIEN, type TrangThai } from '../lib/viec';

export const cx = (...lop: (string | false | null | undefined)[]) => lop.filter(Boolean).join(' ');

/* ── Avatar ── */
// Chữ trắng trên avatar phải đạt 4.5:1. Màu cũ không đạt (đã lưu trong D1 cho tài khoản tạo trước) được đổi LÚC HIỂN
// THỊ — khỏi phải sửa dữ liệu: cam #f6821f 2.58 → #c2410c 5.18 · ngọc #0a8f8f 3.93 → #0f766e 5.47.
const MAU_DAT: Record<string, string> = { '#f6821f': '#c2410c', '#0a8f8f': '#0f766e', '#8c8c96': '#6b6b75' };
const mauAvatar = (m?: string | null) => (m ? MAU_DAT[m.toLowerCase()] || m : '#6b6b75');
function chuCai(ten: string) {
  const tu = ten.trim().split(/\s+/);
  return ((tu.length > 1 ? tu[tu.length - 2][0] : '') + (tu[tu.length - 1]?.[0] || '?')).toUpperCase();
}

export function Avatar({ nguoi, co = 24, title }: { nguoi?: Pick<NguoiDung, 'ten' | 'mau'> | null; co?: number; title?: string }) {
  const ten = nguoi?.ten || '?';
  return (
    <span
      title={title ?? ten}
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-2 ring-mat select-none"
      style={{ width: co, height: co, fontSize: Math.max(9, co * 0.4), background: mauAvatar(nguoi?.mau) }}
    >
      {chuCai(ten)}
    </span>
  );
}

export function NhomAvatar({ ds, toi_da = 3, co = 22 }: { ds: (Pick<NguoiDung, 'ten' | 'mau'> | undefined)[]; toi_da?: number; co?: number }) {
  const hien = ds.filter(Boolean).slice(0, toi_da);
  const du = ds.length - hien.length;
  if (!ds.length) return null;
  return (
    <span className="inline-flex items-center -space-x-1.5" title={ds.map((n) => n?.ten).join(', ')}>
      {hien.map((n, i) => <Avatar key={i} nguoi={n} co={co} title="" />)}
      {du > 0 && (
        <span className="inline-flex items-center justify-center rounded-full bg-mat-noi text-[10px] font-semibold text-chu-phu ring-2 ring-mat" style={{ width: co, height: co }}>
          +{du}
        </span>
      )}
    </span>
  );
}

/* ── Trạng thái ── */
export function ChamTrangThai({ tt, co = 8 }: { tt: TrangThai; co?: number }) {
  const t = thongTinTt(tt);
  return <span className="inline-block shrink-0 rounded-full" style={{ width: co, height: co, background: t.mau }} />;
}

export function ChipTrangThai({ tt }: { tt: TrangThai }) {
  const t = thongTinTt(tt);
  return (
    <span
      className="inline-flex h-6 items-center gap-1.5 rounded-full border px-2 text-[12px] font-medium whitespace-nowrap text-chu"
      style={{ borderColor: `color-mix(in srgb, ${t.mau} 40%, transparent)`, background: `color-mix(in srgb, ${t.mau} 10%, transparent)` }}
    >
      <ChamTrangThai tt={tt} co={6} />
      {t.ten}
    </span>
  );
}

/* ── Ưu tiên ── */
export function BieuTuongUuTien({ uu_tien, co_chu = false }: { uu_tien: string | null; co_chu?: boolean }) {
  const u = UU_TIEN.find((x) => x.ma === uu_tien);
  if (!u) return co_chu ? <span className="text-chu-mo">—</span> : null;
  const Icon = { P0: AlertTriangle, P1: ChevronsUp, P2: Minus, P3: ArrowDown }[u.ngan] ?? ArrowUp;
  const mau = { P0: 'var(--do)', P1: 'var(--cam)', P2: 'var(--chu-phu)', P3: 'var(--chu-mo)' }[u.ngan];
  // Màu chỉ ở BIỂU TƯỢNG; chữ luôn màu mực (cam trên nền trắng chỉ 2.58:1 — axe-core báo).
  return (
    <span className="inline-flex items-center gap-1 text-[12px] font-medium text-chu" title={`Ưu tiên ${u.ten} (${u.ngan})`}>
      <Icon size={14} strokeWidth={2.4} style={{ color: mau }} />
      {co_chu && <span>{u.ten}</span>}
    </span>
  );
}

/* ── Tag / nhãn — chữ luôn là màu mực (đọc được với mọi màu nhãn), màu nhãn chỉ tô viền + nền ── */
export function ChipTag({ ten, mau, onXoa }: { ten: string; mau?: string; onXoa?: () => void }) {
  const m = mau ? '#' + mau : 'var(--chu-mo)';
  return (
    <span
      className="inline-flex h-5 max-w-full items-center gap-1 rounded border px-1.5 text-[11px] font-medium whitespace-nowrap text-chu-phu"
      style={{ borderColor: `color-mix(in srgb, ${m} 45%, var(--vien))`, background: `color-mix(in srgb, ${m} 12%, var(--mat))` }}
    >
      <span className="truncate">{ten}</span>
      {onXoa && (
        <button type="button" onClick={onXoa} className="-mr-0.5 rounded hover:bg-black/10" aria-label={`Gỡ ${ten}`}>
          <X size={11} />
        </button>
      )}
    </span>
  );
}

/* ── Tiến độ ── */
export function ThanhTienDo({ pt, rong = 'w-20', hien_so = true }: { pt: number | null; rong?: string; hien_so?: boolean }) {
  if (pt === null) return <span className="text-[12px] text-chu-mo">—</span>;
  const mau = pt >= 100 ? 'var(--luc)' : pt >= 50 ? 'var(--xanh)' : 'var(--cam)';
  // Chế độ GIÃN (w-full / flex-1): khung ngoài phải chiếm hết chỗ, thanh lấy phần còn lại. Khung ngoài
  // inline-flex co theo nội dung thì thanh con "w-full" rộng bằng 0 — lỗi đã thấy trên trang OKR 28/09.
  const gian = rong === 'w-full' || rong === 'flex-1';
  return (
    <span className={cx('inline-flex items-center gap-2', gian && 'flex w-full')}>
      <span className={cx('h-1.5 overflow-hidden rounded-full bg-mat-noi', gian ? 'min-w-0 flex-1' : rong)}>
        <span className="block h-full rounded-full transition-all" style={{ width: `${Math.min(100, pt)}%`, background: mau }} />
      </span>
      {hien_so && <span className="w-8 shrink-0 text-[12px] text-chu-phu tabular-nums">{pt}%</span>}
    </span>
  );
}

/* ── Trống ── */
export function Trong({ icon, tieu_de, mo_ta, hanh_dong }: { icon?: ReactNode; tieu_de: string; mo_ta?: string; hanh_dong?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      {icon && <div className="mb-1 text-chu-mo">{icon}</div>}
      <div className="font-semibold">{tieu_de}</div>
      {mo_ta && <div className="max-w-sm text-[13px] text-chu-phu">{mo_ta}</div>}
      {hanh_dong && <div className="mt-2">{hanh_dong}</div>}
    </div>
  );
}

export function Xuong({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded bg-mat-noi', className)} />;
}

/* ── Popover: nút + bảng nổi, đóng khi bấm ra ngoài / Esc ── */
export function Popover({
  nut,
  children,
  can = 'trai',
  rong = 'w-64',
  khoa = false,
  len = false,
  moSan = false,
}: {
  nut: (mo: boolean, bat: () => void) => ReactNode;
  children: (dong: () => void) => ReactNode;
  can?: 'trai' | 'phai';
  rong?: string;
  khoa?: boolean;
  len?: boolean; // mở LÊN trên nút (nút nằm sát đáy màn hình, vd thanh sửa hàng loạt)
  moSan?: boolean; // mở ngay lúc dựng (chỉ đọc một lần): nút "＋ Thêm" dựng chip rồi mở luôn bảng chọn của nó
}) {
  const [mo, setMo] = useState(moSan && !khoa);
  const ref = useRef<HTMLDivElement>(null);
  const bang = useRef<HTMLDivElement>(null);
  const [dich, setDich] = useState<number | null>(null);
  // Trong vùng đánh dấu data-bien-popover (nội dung ngăn việc), bảng canh TRÁI được KẸP vào trong vùng đó — chip nằm cuối dải / màn hẹp không bị khung cuộn cắt mất bảng chọn.
  // Đo sau MỖI lần vẽ khi đang mở (ô chọn nhiều lựa chọn giữ bảng mở trong lúc chip nở ra hay xuống dòng). Đo trước khi vẽ nên không nháy; ngoài vùng đánh dấu giữ cách canh cũ.
  useLayoutEffect(() => {
    if (!mo || can !== 'trai') return;
    const goc = ref.current;
    const b = bang.current;
    const bien = goc?.closest('[data-bien-popover]');
    if (!goc || !b || !bien) { setDich(null); return; }
    const khung = bien.getBoundingClientRect();
    const trai = khung.left + 4;
    const phai = khung.left + bien.clientWidth - 4;
    const x = goc.getBoundingClientRect().left;
    setDich(Math.round(Math.min(Math.max(x, trai), Math.max(trai, phai - b.offsetWidth)) - x));
  });
  // Mở sẵn (nút "＋ Thêm" dựng chip rồi mở luôn): mục menu vừa bấm đã bị gỡ khỏi trang nên con trỏ rơi về body → đưa vào phần tử đầu tiên của bảng (ô đã autoFocus thì thôi).
  useEffect(() => {
    if (!moSan || khoa) return;
    const b = bang.current;
    if (b && !b.contains(document.activeElement)) b.querySelector<HTMLElement>('input,select,textarea,button')?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ lúc dựng
  }, []);
  // Đóng bảng mà con trỏ không còn ở đâu (mục vừa chọn đã bị gỡ khỏi trang) → trả về nút mở bảng, khỏi rơi về đầu trang.
  const da_mo = useRef(mo);
  useEffect(() => {
    if (da_mo.current && !mo && (!document.activeElement || document.activeElement === document.body)) ref.current?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
    da_mo.current = mo;
  }, [mo]);
  useEffect(() => {
    if (!mo) return;
    const ngoai = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMo(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setMo(false); } };
    document.addEventListener('mousedown', ngoai);
    document.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('mousedown', ngoai); document.removeEventListener('keydown', esc, true); };
  }, [mo]);
  return (
    <div ref={ref} className="relative inline-block">
      {nut(mo, () => !khoa && setMo((x) => !x))}
      {mo && (
        <div ref={bang} style={can === 'trai' && dich !== null ? { left: dich } : undefined} className={cx('hien absolute z-40 rounded-lg border border-vien bg-mat p-1 shadow-noi', len ? 'bottom-full mb-1' : 'mt-1', rong, can === 'phai' ? 'right-0' : dich === null && 'left-0')}>
          {children(() => setMo(false))}
        </div>
      )}
    </div>
  );
}

// `sang`: dòng đang trỏ bằng phím ↑↓ (ô chọn có điều khiển bàn phím).
export function MucMenu({ chon, onClick, children, nguy, sang }: { chon?: boolean; onClick: () => void; children: ReactNode; nguy?: boolean; sang?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-mat-noi',
        chon && 'bg-xanh-nhat text-xanh',
        sang && !chon && 'bg-mat-noi',
        nguy && 'text-do',
      )}
    >
      {children}
    </button>
  );
}

/* ── Hộp thoại ── */
export function HopThoai({ mo, onDong, tieu_de, children, rong = 'max-w-lg' }: { mo: boolean; onDong: () => void; tieu_de: string; children: ReactNode; rong?: string }) {
  useEffect(() => {
    if (!mo) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onDong();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [mo, onDong]);
  if (!mo) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[8vh] backdrop-blur-[1px]" onMouseDown={(e) => e.target === e.currentTarget && onDong()}>
      <div role="dialog" aria-modal="true" aria-label={tieu_de} className={cx('hien w-full rounded-xl border border-vien bg-mat shadow-noi', rong)}>
        <div className="flex items-center justify-between border-b border-vien px-5 py-3">
          <h2 className="text-[15px] font-semibold">{tieu_de}</h2>
          <button className="nut-ma h-7 px-1.5" onClick={onDong} aria-label="Đóng"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-vien bg-mat-2 px-1 font-mono text-[10px] text-chu-mo">{children}</kbd>;
}

export const dinhDangNgay = (iso: string | null, kieu: 'ngan' | 'dai' = 'ngan') => {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
  // Tự định dạng thay vì toLocaleDateString: kết quả của ICU khác nhau giữa trình duyệt/máy ("10/09" vs "10-09").
  const h = (n: number) => String(n).padStart(2, '0');
  const ngay = `${h(d.getDate())}/${h(d.getMonth() + 1)}`;
  return kieu === 'ngan' ? ngay : `${ngay}/${d.getFullYear()} ${h(d.getHours())}:${h(d.getMinutes())}`;
};

export function tuongDoi(iso: string): string {
  const giay = (Date.now() - new Date(iso).getTime()) / 1000;
  if (giay < 60) return 'vừa xong';
  if (giay < 3600) return `${Math.floor(giay / 60)} phút trước`;
  if (giay < 86400) return `${Math.floor(giay / 3600)} giờ trước`;
  if (giay < 86400 * 30) return `${Math.floor(giay / 86400)} ngày trước`;
  return dinhDangNgay(iso, 'dai').split(' ')[0];
}
